// w10 Lola — Persian/Bukharan tulip miniature wedding catalog card.
//
// Paper-invitation feel: outer scarlet foil mat with hand-drawn manuscript
// edge wraps a cream inner card; the focal medallion is a stylised tulip
// (the "lola") on a parchment roundel, evoking a 16th-century folio. Names
// render in Cormorant Garamond; caption sits at the bottom in small caps.
import { Photo, type LayoutCtx } from "./_shared";

export function TulipMiniatureLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  const foil = `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 55%, ${bg} 45%) 50%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: foil,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Tulip manuscript medallion. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 50%, ${bg} 0%, ${accent}12 60%, ${bg})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 0 2px ${bg}, 0 0 0 3px ${accent}33`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "82%", height: "82%" }} aria-hidden>
                {/* Concentric hairlines */}
                <circle cx="50" cy="50" r="40" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.55" />
                <circle cx="50" cy="50" r="34" fill="none" stroke={accent} strokeWidth="0.4" opacity="0.4" />

                {/* Central tulip — three-petal silhouette */}
                <g transform="translate(50 56)">
                  {/* Stem */}
                  <line x1="0" y1="0" x2="0" y2="22" stroke={accent} strokeWidth="1.2" />
                  {/* Outer petals (sides) */}
                  <path
                    d="M-10 -2 Q-14 -16 -3 -22 Q-2 -10 -4 -2 Z"
                    fill={accent}
                    opacity="0.8"
                  />
                  <path
                    d="M10 -2 Q14 -16 3 -22 Q2 -10 4 -2 Z"
                    fill={accent}
                    opacity="0.8"
                  />
                  {/* Centre petal */}
                  <path
                    d="M-5 -2 Q-6 -18 0 -24 Q6 -18 5 -2 Z"
                    fill={accent}
                  />
                  {/* Leaves */}
                  <path d="M0 6 Q-12 8 -16 18 Q-8 14 0 12 Z" fill={accent} opacity="0.7" />
                  <path d="M0 10 Q12 12 16 22 Q8 18 0 16 Z" fill={accent} opacity="0.7" />
                </g>

                {/* Small ornaments at top + bottom */}
                <circle cx="50" cy="14" r="1.6" fill={accent} />
                <circle cx="50" cy="86" r="1.6" fill={accent} opacity="0.6" />
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
